import { ref, get, update, remove } from "firebase/database";
import { db, storage } from "./firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { verifyAuth } from "./authFunction";

const TICKETS_PATH = "contacthq/tickets";

export const getAdminTicketsList = async () => {
  try {
    const auth = await verifyAuth();
    if (!auth?.success) {
      return {
        success: false,
        error: "Authentication failed or profile not loaded.",
      };
    }

    const ticketsRef = ref(db, TICKETS_PATH);
    const snapshot = await get(ticketsRef);

    if (!snapshot.exists()) return { success: true, data: [] };

    const ticketsArray = Object.values(snapshot.val());
    ticketsArray.sort(
      (a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated),
    );

    return { success: true, data: ticketsArray };
  } catch (error) {
    console.error("🔥 Admin Fetch Error:", error);
    return { success: false, error: error.message };
  }
};

export const adminReplyTicket = async (ticketId, messageText, files = []) => {
  try {
    const auth = await verifyAuth();
    if (!auth?.success) {
      return { success: false, error: "Unauthorized: Admin access required." };
    }

    // 2. Safely destructure after checking existence
    const { adminId, name } = auth.adminData;
    if (!adminId)
      return { success: false, error: "Admin ID missing from session." };

    let attachments = [];
    if (files?.length > 0) {
      const uploadPromises = files.map(async (file) => {
        const pRef = storageRef(
          storage,
          `${TICKETS_PATH}/${ticketId}/admin_${Date.now()}_${file.name}`,
        );
        await uploadBytes(pRef, file);
        return {
          name: file.name,
          size: (file.size / 1024).toFixed(1) + " KB",
          type: file.type,
          url: await getDownloadURL(pRef),
        };
      });
      attachments = (await Promise.all(uploadPromises)).filter(Boolean);
    }

    const newMessage = {
      id: `msg_${Date.now()}`,
      senderId: adminId,
      senderName: name || "System Admin",
      role: "admin",
      message: messageText,
      attachments,
      timestamp: new Date().toISOString(),
      type: "text",
      status: "unseen",
    };

    const ticketRef = ref(db, `${TICKETS_PATH}/${ticketId}`);
    const snapshot = await get(ticketRef);
    if (!snapshot.exists())
      return { success: false, error: "Ticket not found" };

    const currentData = snapshot.val();
    await update(ticketRef, {
      messages: [...(currentData.messages || []), newMessage],
      status: "In Progress",
      lastUpdated: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const changeTicketStatus = async (ticketId, newStatus) => {
  try {
    const auth = await verifyAuth();
    if (!auth.success)
      return { success: false, error: "Unauthorized" };

    const ticketRef = ref(db, `${TICKETS_PATH}/${ticketId}`);
    await update(ticketRef, {
      status: newStatus,
      lastUpdated: new Date().toISOString(),
    });

    return { success: true, message: `Ticket ${newStatus} successfully.` };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteTicket = async (ticketId) => {
  try {
    const auth = await verifyAuth();
    if (!auth.success || auth.companyData.role !== "admin")
      return { success: false, error: "Unauthorized" };

    await remove(ref(db, `${TICKETS_PATH}/${ticketId}`));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const markMessagesAsSeen = async (ticketId) => {
  try {
    const auth = await verifyAuth();
    if (!auth.success) {
      return { success: false, error: "Unauthorized" };
    }

    const ticketRef = ref(db, `${TICKETS_PATH}/${ticketId}`);
    const snapshot = await get(ticketRef);

    if (!snapshot.exists())
      return { success: false, error: "Ticket not found" };

    const { messages = [] } = snapshot.val();
    let updatesRequired = false;

    // Iterate through messages to find unseen items from the user
    const updatedMessages = messages.map((msg) => {
      // If the message was sent by a user and admin hasn't 'seen' it yet
      if (msg.role === "user" && msg.status === "unseen") {
        updatesRequired = true;
        return { ...msg, status: "seen" };
      }
      return msg;
    });

    if (updatesRequired) {
      await update(ticketRef, {
        messages: updatedMessages,
        // Optional: Keep lastUpdated in sync with the read action
        lastUpdated: new Date().toISOString(),
      });
    }

    return { success: true };
  } catch (error) {
    console.error("🔥 Mark Seen Error:", error);
    return { success: false, error: error.message };
  }
};
